/**
 * DbToast - 消息提示组件
 *
 * 特性：
 * - 支持命令式调用（Toast.show / Toast.success / Toast.error 等）
 * - 支持声明式使用（<DbToast visible={} />）
 * - 多种类型：success, error, warning, info, loading
 * - 可自定义图标、位置、样式、持续时间
 * - 支持遮罩层
 */

import React, { useEffect, useRef, useCallback } from 'react';
import {
    View,
    Text,
    Modal,
    Animated,
    TouchableWithoutFeedback,
    Easing,
} from 'react-native';
import {
    DbToastProps,
    ToastOptions,
    ToastInstance,
    ToastMethods,
    ToastType,
    ToastIconConfig,
} from './DbToast.types';
import {
    styles,
    TOAST_CONSTANTS,
    TOAST_TYPE_COLORS,
    TOAST_TYPE_ICONS,
    getPositionStyle,
} from './DbToast.styles';
import DbIcon from '../DbIcon';

// ==================== Toast 管理器（全局状态） ====================

type ToastState = ToastOptions & { visible: boolean };
type ToastListener = (state: ToastState) => void;

class ToastManager {
    private listeners: Set<ToastListener> = new Set();
    private state: ToastState = { visible: false, message: '' };
    private timer: ReturnType<typeof setTimeout> | null = null;

    subscribe(listener: ToastListener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    private notify() {
        this.listeners.forEach(listener => listener(this.state));
    }

    private clearTimer() {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }

    show(options: ToastOptions): ToastInstance {
        this.clearTimer();
        
        const duration = options.duration ?? 
            (options.type === 'loading' ? TOAST_CONSTANTS.loadingDuration : TOAST_CONSTANTS.defaultDuration);

        this.state = { ...options, visible: true };
        this.notify();

        // 自动关闭
        if (duration > 0) {
            this.timer = setTimeout(() => {
                this.hide();
            }, duration);
        }

        return {
            close: () => this.hide(),
        };
    }

    hide() {
        this.clearTimer();
        this.state = { ...this.state, visible: false };
        this.notify();
        this.state.onClose?.();
    }

    getState() {
        return this.state;
    }
}

const toastManager = new ToastManager();

// ==================== Loading 动画组件 ====================

const LoadingSpinner: React.FC<{ color?: string; size?: number }> = ({ 
    color = '#FFFFFF', 
    size = 24 
}) => {
    const spinValue = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        const animation = Animated.loop(
            Animated.timing(spinValue, {
                toValue: 1,
                duration: 1000,
                easing: Easing.linear,
                useNativeDriver: true,
            })
        );
        animation.start();
        return () => animation.stop();
    }, [spinValue]);

    const spin = spinValue.interpolate({
        inputRange: [0, 1],
        outputRange: ['0deg', '360deg'],
    });

    return (
        <View style={styles.loadingContainer}>
            <Animated.View
                style={[
                    styles.loadingSpinner,
                    {
                        width: size,
                        height: size,
                        borderRadius: size / 2,
                        borderTopColor: color,
                        transform: [{ rotate: spin }],
                    },
                ]}
            />
        </View>
    );
};

// ==================== DbToast 组件 ====================

export const DbToast: React.FC<DbToastProps> & ToastMethods = ({
    visible,
    message,
    type = 'info',
    position = 'center',
    icon,
    showIcon = true,
    mask = false,
    maskColor = TOAST_CONSTANTS.maskColor,
    onClose,
    style,
    textStyle,
    backgroundColor = TOAST_CONSTANTS.backgroundColor,
    textColor = TOAST_CONSTANTS.textColor,
    borderRadius = TOAST_CONSTANTS.borderRadius,
    children,
}) => {
    const fadeAnim = useRef(new Animated.Value(0)).current;
    const scaleAnim = useRef(new Animated.Value(0.8)).current;

    useEffect(() => {
        if (visible) {
            Animated.parallel([
                Animated.timing(fadeAnim, {
                    toValue: 1,
                    duration: 200,
                    useNativeDriver: true,
                }),
                Animated.spring(scaleAnim, {
                    toValue: 1,
                    tension: 100,
                    friction: 8,
                    useNativeDriver: true,
                }),
            ]).start();
        } else {
            Animated.parallel([
                Animated.timing(fadeAnim, {
                    toValue: 0,
                    duration: 150,
                    useNativeDriver: true,
                }),
                Animated.timing(scaleAnim, {
                    toValue: 0.8,
                    duration: 150,
                    useNativeDriver: true,
                }),
            ]).start();
        }
    }, [visible, fadeAnim, scaleAnim]);

    // 渲染图标
    const renderIcon = () => {
        if (!showIcon) return null;

        // 自定义 ReactNode
        if (icon && React.isValidElement(icon)) {
            return <View style={styles.iconContainer}>{icon}</View>;
        }

        // loading 类型使用旋转动画
        if (type === 'loading') {
            return (
                <View style={styles.iconContainer}>
                    <LoadingSpinner color={textColor} />
                </View>
            );
        }

        // 自定义图标配置或默认图标
        const iconConfig = (icon as ToastIconConfig) || TOAST_TYPE_ICONS[type];
        if (!iconConfig || !iconConfig.name) return null;

        const iconColor = (icon as ToastIconConfig)?.color || TOAST_TYPE_COLORS[type] || textColor;

        return (
            <View style={styles.iconContainer}>
                <DbIcon
                    name={iconConfig.name}
                    family={(iconConfig.family as any) || 'Ionicons'}
                    size={(icon as ToastIconConfig)?.size || TOAST_CONSTANTS.iconSize}
                    color={iconColor}
                />
            </View>
        );
    };

    const positionStyle = getPositionStyle(position);

    if (!visible) return null;

    return (
        <Modal
            visible={visible}
            transparent
            animationType="none"
            statusBarTranslucent
            onRequestClose={onClose}
        >
            <TouchableWithoutFeedback onPress={mask ? onClose : undefined}>
                <View style={[styles.overlay, positionStyle, { backgroundColor: maskColor }]}>
                    <TouchableWithoutFeedback>
                        <Animated.View
                            style={[
                                styles.container,
                                {
                                    backgroundColor,
                                    borderRadius,
                                    opacity: fadeAnim,
                                    transform: [{ scale: scaleAnim }],
                                },
                                style,
                            ]}
                        >
                            {children || (
                                <>
                                    {renderIcon()}
                                    {message && (
                                        <Text
                                            style={[
                                                styles.message,
                                                { color: textColor },
                                                textStyle,
                                            ]}
                                        >
                                            {message}
                                        </Text>
                                    )}
                                </>
                            )}
                        </Animated.View>
                    </TouchableWithoutFeedback>
                </View>
            </TouchableWithoutFeedback>
        </Modal>
    );
};

// ==================== 命令式 API ====================

const normalizeOptions = (
    messageOrOptions: string | ToastOptions,
    duration?: number,
    type?: ToastType
): ToastOptions => {
    if (typeof messageOrOptions === 'string') {
        return {
            message: messageOrOptions,
            duration,
            type,
        };
    }
    return { ...messageOrOptions, type: messageOrOptions.type || type };
};

DbToast.show = (messageOrOptions, duration) => {
    return toastManager.show(normalizeOptions(messageOrOptions, duration, 'info'));
};

DbToast.success = (messageOrOptions, duration) => {
    return toastManager.show(normalizeOptions(messageOrOptions, duration, 'success'));
};

DbToast.error = (messageOrOptions, duration) => {
    return toastManager.show(normalizeOptions(messageOrOptions, duration, 'error'));
};

DbToast.warning = (messageOrOptions, duration) => {
    return toastManager.show(normalizeOptions(messageOrOptions, duration, 'warning'));
};

DbToast.info = (messageOrOptions, duration) => {
    return toastManager.show(normalizeOptions(messageOrOptions, duration, 'info'));
};

DbToast.loading = (messageOrOptions) => {
    const options: Partial<ToastOptions> = typeof messageOrOptions === 'string'
        ? { message: messageOrOptions }
        : messageOrOptions || {};
    return toastManager.show({
        ...options,
        type: 'loading',
        message: options.message || '加载中...',
        duration: 0,
        mask: options.mask ?? true,
    });
};

DbToast.hide = () => {
    toastManager.hide();
};

DbToast.clear = DbToast.hide;

// ==================== Toast Provider（命令式调用需要） ====================

export const DbToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, setState] = React.useState<ToastOptions & { visible: boolean }>({
        visible: false,
        message: '',
    });

    useEffect(() => {
        const unsubscribe = toastManager.subscribe(setState);
        return () => { unsubscribe(); };
    }, []);

    const handleClose = useCallback(() => {
        toastManager.hide();
    }, []);

    return (
        <>
            {children}
            <DbToast
                visible={state.visible}
                message={state.message}
                type={state.type}
                position={state.position}
                icon={state.icon}
                showIcon={state.showIcon}
                mask={state.mask}
                maskColor={state.maskColor}
                onClose={handleClose}
                style={state.style}
                textStyle={state.textStyle}
                backgroundColor={state.backgroundColor}
                textColor={state.textColor}
                borderRadius={state.borderRadius}
            >
                {state.content}
            </DbToast>
        </>
    );
};

DbToast.displayName = 'DbToast';

export default DbToast;
export type {
    DbToastProps,
    ToastOptions,
    ToastInstance,
    ToastMethods,
    ToastType,
    ToastPosition,
    ToastIconConfig,
} from './DbToast.types';
